import React from 'react';
import { MaterialIcon, dashboardIcons } from './IconHelper';

interface AdminDashboardProps {
  activeTab: string;
  data?: any;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ activeTab, data }) => {
  const dashboardData = data?.admin || {
    totalUsers: '2,543',
    systemHealth: '99.8%',
    security: '✅ Secure',
    auditLogs: '45.2K',
    newUsers: '125 users registered this week',
    systemAlerts: 'All systems operational',
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          <MaterialIcon icon="settings" size="large" />
          Admin Dashboard
        </h1>
        <p className="text-slate-600">Kelola sistem platform dan user management</p>
      </div>

      {activeTab === 'dashboard' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-300 rounded-lg p-6">
              <div className="text-slate-600 text-sm font-semibold mb-1 flex items-center gap-1">
                <MaterialIcon icon="people" /> Total Users
              </div>
              <div className="text-2xl font-bold text-slate-900">{dashboardData.totalUsers}</div>
              <div className="text-xs text-slate-600 mt-2">Active</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300 rounded-lg p-6">
              <div className="text-blue-600 text-sm font-semibold mb-1 flex items-center gap-1">
                <MaterialIcon icon="health_and_safety" /> System Health
              </div>
              <div className="text-2xl font-bold text-blue-900">{dashboardData.systemHealth}</div>
              <div className="text-xs text-blue-600 mt-2">Uptime</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-300 rounded-lg p-6">
              <div className="text-emerald-600 text-sm font-semibold mb-1 flex items-center gap-1">
                <MaterialIcon icon="lock" /> Security
              </div>
              <div className="text-2xl font-bold text-emerald-900 flex items-center gap-1">
                Secure
              </div>
              <div className="text-xs text-emerald-600 mt-2">All Clear</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-300 rounded-lg p-6">
              <div className="text-purple-600 text-sm font-semibold mb-1 flex items-center gap-1">
                <MaterialIcon icon="history" /> Audit Logs
              </div>
              <div className="text-2xl font-bold text-purple-900">{dashboardData.auditLogs}</div>
              <div className="text-xs text-purple-600 mt-2">This Month</div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MaterialIcon icon="people" /> 
            <h2 className="text-lg font-semibold">User Management</h2>
          </div>
          <div className="space-y-3">
            {[
              { name: 'Rina Wijaya', email: 'rina@aco.com', role: 'investor_micro', status: 'Active', kyc: 'Verified' },
              { name: 'Budi Santoso', email: 'budi@aco.com', role: 'investor_enterprise', status: 'Active', kyc: 'Verified' },
              { name: 'Dimas Pratama', email: 'dimas@aco.com', role: 'project_owner', status: 'Active', kyc: 'Verified' },
              { name: 'Pak Salim Hartono', email: 'salim@aco.com', role: 'muzakki', status: 'Active', kyc: 'Verified' },
              { name: 'Tari Budiono', email: 'tari@aco.com', role: 'finance_officer', status: 'Active', kyc: 'Verified' },
            ].map((user, idx) => (
              <div key={idx} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">{user.role}</span>
                      <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">{user.kyc}</span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 bg-emerald-100 text-emerald-700 rounded">{user.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'projects' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MaterialIcon icon="folder" /> 
            <h2 className="text-lg font-semibold">Kelola Proyek</h2>
          </div>
          <div className="space-y-3">
            {[
              { name: 'Startup Tech Medan', owner: 'Dimas P', status: 'Active', investors: 12, amount: 'Rp 350M' },
              { name: 'Organic Coffee Beans', owner: 'Dimas P', status: 'Active', investors: 8, amount: 'Rp 200M' },
              { name: 'Solar Energy Sumatra', owner: 'Dimas P', status: 'Active', investors: 10, amount: 'Rp 450M' },
              { name: 'Fishery Cooperative', owner: 'Dimas P', status: 'Approved', investors: 6, amount: 'Rp 150M' },
            ].map((proj, idx) => (
              <div key={idx} className="p-4 border border-slate-200 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-slate-900">{proj.name}</p>
                    <p className="text-xs text-slate-600 mt-1">Owner: {proj.owner}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${
                    proj.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                  }`}>{proj.status}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>{proj.investors} Investors</span>
                  <span className="font-semibold">{proj.amount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MaterialIcon icon="history" /> 
            <h2 className="text-lg font-semibold">Audit Log</h2>
          </div>
          <div className="space-y-2">
            {[
              { time: '2026-05-20 14:32', user: 'Rina Wijaya', action: 'Login', status: 'Success', ip: '192.168.1.100' },
              { time: '2026-05-20 14:28', user: 'Budi Santoso', action: 'Investment Created', status: 'Success', ip: '192.168.1.101' },
              { time: '2026-05-20 14:15', user: 'Tari Budiono', action: 'Disbursement Verified', status: 'Success', ip: '192.168.1.102' },
              { time: '2026-05-20 13:45', user: 'Pak Salim', action: 'Zakat Payment', status: 'Success', ip: '192.168.1.103' },
              { time: '2026-05-20 13:30', user: 'Dimas P', action: 'Project Updated', status: 'Success', ip: '192.168.1.104' },
            ].map((log, idx) => (
              <div key={idx} className="p-3 border border-slate-200 rounded-lg text-xs hover:bg-slate-50 transition">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-slate-900">{log.action}</p>
                    <p className="text-slate-600">{log.user} • {log.time}</p>
                  </div>
                  <span className="text-slate-600">{log.ip}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MaterialIcon icon="settings" /> 
            <h2 className="text-lg font-semibold">Konfigurasi Sistem</h2>
          </div>
          <div className="space-y-4">
            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-semibold text-slate-900 mb-3">Platform Settings</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Maintenance Mode</span>
                  <label className="flex items-center gap-2"><input type="checkbox" className="w-4 h-4" /><span>Off</span></label>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">API Rate Limit</span>
                  <input type="number" className="w-20 px-2 py-1 border border-slate-200 rounded text-xs" defaultValue="1000" />
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Session Timeout (min)</span>
                  <input type="number" className="w-20 px-2 py-1 border border-slate-200 rounded text-xs" defaultValue="30" />
                </div>
              </div>
            </div>
            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-semibold text-slate-900 mb-3">Security Settings</p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                  <span className="text-sm text-slate-600">Enable 2FA for all users</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                  <span className="text-sm text-slate-600">SSL/TLS Encryption</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                  <span className="text-sm text-slate-600">IP Whitelisting</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
